import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { ColumnOptions, MarketPhase } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { WidgetColumnOptions } from '@thinkalpha/ui-types/contracts/workspace';
import { isEqual } from 'es-toolkit';
import type { ColumnOptionsEvents, ColumnOptionsSignal } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

export const columnOptionsStateSchema = z.object({
    columnOptions: z.custom<WidgetColumnOptions>((value) => typeof value === 'object' && value !== null).optional(),
});

export type ColumnOptionsState = z.output<typeof columnOptionsStateSchema>;

export type ColumnOptionsModelEventBus = EventBus<ColumnOptionsSignal, ColumnOptionsEvents>;

interface ColumnOptionsModelInit {
    persisted: ColumnOptionsState;
}

export interface ColumnOptionsModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: ColumnOptionsModelEventBus;
    readonly columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    toJSON(): ColumnOptionsState;
    markClean(): void;
}

/**
 * Owns a widget's persisted per-widget column options (market phases,
 * adjustments, preheat) and the column-options EventBus contract consumed by
 * the settings menu.
 */
export const ColumnOptionsModel: ModelConstructor<ColumnOptionsModelShape, [ColumnOptionsModelInit]> = createModel<
    ColumnOptionsModelShape,
    [ColumnOptionsModelInit]
>((init: ColumnOptionsModelInit) => {
    const columnOptions: Signal<WidgetColumnOptions | undefined> = signal(init.persisted.columnOptions);
    const cleanColumnOptions: Signal<WidgetColumnOptions | undefined> = signal(init.persisted.columnOptions);

    const setColumnOptions = (next: WidgetColumnOptions | undefined): void => {
        columnOptions.value = next;
    };

    const eventBus: ColumnOptionsModelEventBus = EventBusBuilder.create()
        .addSignal('widget::columnOptions', () => computed<Partial<ColumnOptions>>(() => columnOptions.value ?? {}))
        .addEvent('widget::toggleMarketPhase', ({ phase, enabled }) => {
            const currentPhases = columnOptions.value?.marketPhases ?? [];
            const nextPhases = enabled
                ? currentPhases.includes(phase)
                    ? currentPhases
                    : [...currentPhases, phase]
                : currentPhases.filter((p: MarketPhase) => p !== phase);
            setColumnOptions({
                ...columnOptions.value,
                marketPhases: nextPhases.length > 0 ? nextPhases : undefined,
            });
        })
        .addEvent('widget::setAdjustments', ({ mode }) => {
            const next: WidgetColumnOptions = { ...columnOptions.value };
            if (mode !== undefined) next.adjustments = mode;
            else delete next.adjustments;
            setColumnOptions(next);
        })
        .addEvent('widget::setPreheat', ({ value }) => {
            const next: WidgetColumnOptions = { ...columnOptions.value };
            if (value !== undefined) next.preheat = value;
            else delete next.preheat;
            setColumnOptions(next);
        })
        .addEvent('widget::clearColumnOptions', () => {
            setColumnOptions({});
        })
        .build();

    return {
        dirty: computed(() => !isEqual(columnOptions.value, cleanColumnOptions.value)),
        eventBus,
        columnOptions: computed(() => columnOptions.value ?? {}),
        toJSON(): ColumnOptionsState {
            return { columnOptions: columnOptions.value };
        },
        markClean(): void {
            cleanColumnOptions.value = columnOptions.value;
        },
    };
});

export type ColumnOptionsModel = Model<ColumnOptionsModelShape>;
